import React, { useState, useMemo, useContext, useEffect } from 'react';
import apiClient, { getAuthHeaders, withApiPath } from '../utils/apiClient';
import moment from 'moment';
import {
  FiSearch,
  FiChevronDown,
  FiChevronUp
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { LoadContext } from './loadContext';
import Surface from './ui/Surface';
import Button from './ui/Button';
import ShipmentSummaryDrawer from './loadboard/ShipmentSummaryDrawer';
import { getReferenceValue } from '../utils/shipmentReferences';
import styles from './LoadBoard.module.css';

const tabs = [
  { key: 'ftl', label: 'FTL Load Board' },
  { key: 'quote', label: 'Quotes & Bookings' },
  { key: 'accountsPayable', label: 'Accounts Payable' },
  { key: 'claims', label: 'Claims' },
  { key: 'stats', label: 'Reporting' }
];

const columns = [
  { key: 'bol_number', label: 'BOL' },
  { key: 'pickup_date', label: 'Pickup date' },
  { key: 'lane', label: 'Lane' },
  { key: 'carrier_name', label: 'Carrier' },
  { key: 'status', label: 'Status' },
  { key: 'current_location', label: 'Location' },
  { key: 'delivery_date', label: 'Delivery date' },
  { key: 'last_remark', label: 'Last remark' }
];

const sortableColumns = new Set(['pickup_date', 'delivery_date', 'status']);
const operationalFilters = [
  "Today's Loads - Picking Up",
  "Today's Loads - Delivering",
  "Tomorrow's Loads - Picking Up",
  "Tomorrow's Loads - Delivering",
  'Over the Road',
  "Yesterday's Loads - Delivering",
  'Delivered Loads',
  'Ready for Bill Loads',
  'Completed Loads',
];

export const formatDate = (value) => {
  if (!value) return '—';
  const date = moment(value);
  return date.isValid() ? date.format('MMM D, YYYY HH:mm') : value;
};

export const getBoundaryStop = (load, stopType) => {
  const stops = Array.isArray(load?.stops) ? load.stops : [];
  const canonicalStops = stops
    .filter((stop) => ['pickup', 'delivery'].includes(stop?.stop_type))
    .sort((left, right) => Number(left.sequence) - Number(right.sequence));

  if (!canonicalStops.length) return null;
  const matchingStops = canonicalStops.filter((stop) => stop.stop_type === stopType);
  if (!matchingStops.length) return undefined;
  return stopType === 'pickup' ? matchingStops[0] : matchingStops[matchingStops.length - 1];
};

export const formatStopSchedule = (load, stopType) => {
  const stop = getBoundaryStop(load, stopType);
  // null means structured stops are unavailable, so legacy dates remain a
  // compatibility fallback. undefined means structured routing exists but has
  // no boundary of this type; do not allow legacy data to override it.
  if (stop === null) {
    return formatDate(stopType === 'pickup' ? load?.pickup_date : load?.delivery_date);
  }
  if (!stop) return '—';

  const dateValue = stop.scheduled_date || stop.window_start || stop.window_end;
  if (!dateValue) return '—';
  const date = moment(dateValue);
  if (!date.isValid()) return '—';

  const start = stop.window_start ? moment(stop.window_start) : null;
  const end = stop.window_end ? moment(stop.window_end) : null;
  const validStart = start?.isValid() ? start : null;
  const validEnd = end?.isValid() ? end : null;
  const times = validStart && validEnd
    ? `${validStart.format('HH:mm')}–${validEnd.format('HH:mm')}`
    : (validStart || validEnd)?.format('HH:mm');
  return `${date.format('MMM D')}${times ? ` · ${times}` : ''}`;
};

const LoadBoard = () => {
  const {
    loads,
    ftlStats,
    statsStatus,
    fetchFTLLoadboardData
  } = useContext(LoadContext);
  const {
    loads_this_week: loadsThisWeek,
    bookings_confirmed: bookingsConfirmed,
    bookings_total: bookingsTotal,
    loads_with_issues: loadsWithIssues,
    on_time_delivery_percentage: onTimeDeliveryPercentage,
  } = ftlStats || {};

  const [searchQueries, setSearchQueries] = useState(
    Object.fromEntries(columns.map((column) => [column.key, '']))
  );
  const [sortConfig, setSortConfig] = useState(null);
  const [isSearchRowVisible, setIsSearchRowVisible] = useState(false);
  const [selectedTab, setSelectedTab] = useState('ftl');
  const [selectedFilterOption, setSelectedFilterOption] = useState('');
  const [loadDetailsContent, setLoadDetailsContent] = useState({});
  const [expandedLoadId, setExpandedLoadId] = useState(null);
  const [expandedDeliveredLoadId, setExpandedDeliveredLoadId] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState({});
  const [detailErrors, setDetailErrors] = useState({});
  const [showDeliveredLoads, setShowDeliveredLoads] = useState(false);
  const navigate = useNavigate();
  const statValue = (value, suffix = '') => {
    if (statsStatus === 'loading' || statsStatus === 'idle') return '…';
    if (statsStatus === 'error' || value === null || value === undefined) return '—';
    return `${value}${suffix}`;
  };

  useEffect(() => {
    if (selectedTab !== 'ftl') {
      return;
    }
    const accountId = localStorage.getItem('accountId') || sessionStorage.getItem('accountId');
    if (!accountId) {
      return;
    }
    const ordering = sortConfig
      ? `${sortConfig.direction === 'descending' ? '-' : ''}${sortConfig.key}`
      : '';
    fetchFTLLoadboardData(accountId, selectedFilterOption, ordering);
  }, [selectedTab, selectedFilterOption, sortConfig, fetchFTLLoadboardData]);

  const handleSearchInput = (key, value) => {
    setSearchQueries((prev) => ({ ...prev, [key]: value.toLowerCase() }));
  };

  const requestSort = (key) => {
    if (!sortableColumns.has(key)) return;
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    } else if (sortConfig && sortConfig.key === key && sortConfig.direction === 'descending') {
      setSortConfig(null);
      return;
    }
    setSortConfig({ key, direction });
  };

  const sortedLoads = useMemo(() => {
    if (!Array.isArray(loads)) {
      return [];
    }
    // Ordering is performed by the API so it applies to the complete scoped
    // result set rather than only the currently returned page.
    return loads;
  }, [loads]);

  const filteredLoads = useMemo(() => {
    return sortedLoads.filter((load) =>
      columns.every(({ key }) => {
        const value = load[key];
        if (value === undefined || value === null) {
          return true;
        }
        return value.toString().toLowerCase().includes(searchQueries[key]);
      })
    );
  }, [sortedLoads, searchQueries]);

  const deliveredLoads = useMemo(() => {
    if (!Array.isArray(loads)) {
      return [];
    }
    return loads.filter((load) => (load.status || '').toLowerCase() === 'delivered');
  }, [loads]);

  const toggleLoadExpansion = async (loadId) => {
    if (expandedLoadId === loadId) {
      setExpandedLoadId(null);
      return;
    }
    setExpandedLoadId(loadId);
    setLoadingDetails((prev) => ({ ...prev, [loadId]: true }));
    setDetailErrors((prev) => ({ ...prev, [loadId]: false }));
    try {
      const details = await fetchLoadDetails(loadId);
      if (details) {
        setLoadDetailsContent((prev) => ({ ...prev, [loadId]: details }));
      }
    } catch (error) {
      console.error('Error fetching load details:', error);
      setDetailErrors((prev) => ({ ...prev, [loadId]: true }));
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [loadId]: false }));
    }
  };

  const toggleDeliveredLoadExpansion = async (loadId) => {
    if (expandedDeliveredLoadId === loadId) {
      setExpandedDeliveredLoadId(null);
      return;
    }
    setExpandedDeliveredLoadId(loadId);
    setLoadingDetails((prev) => ({ ...prev, [loadId]: true }));
    setDetailErrors((prev) => ({ ...prev, [loadId]: false }));
    try {
      const details = await fetchLoadDetails(loadId);
      if (details) {
        setLoadDetailsContent((prev) => ({ ...prev, [loadId]: details }));
      }
    } catch (error) {
      console.error('Error fetching delivered load details:', error);
      setDetailErrors((prev) => ({ ...prev, [loadId]: true }));
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [loadId]: false }));
    }
  };

  const fetchLoadDetails = async (loadId) => {
    const response = await apiClient.get(withApiPath(`/loads/${loadId}/`), {
      headers: getAuthHeaders(),
    });
    return response.data;
  };

  const handleFilterOptionChange = (option) => {
    setSelectedFilterOption(option);
  };

  const handleOpenWorkspace = async (loadId) => {
    navigate(`/loads/${loadId}`);
  };

  const renderSortIndicator = (key) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <span className={styles.sortNeutral} aria-hidden="true">↕</span>;
    }
    return sortConfig.direction === 'ascending' ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />;
  };

  const renderFTL = () => (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.tabRow}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.tabButton} ${selectedTab === tab.key ? styles.tabActive : ''}`}
              onClick={() => setSelectedTab(tab.key)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className={styles.controls}>
          <div className={styles.controlInput}>
            <FiSearch />
            <input
              type="search"
              placeholder="Search loads"
              value={searchQueries.bol_number}
              onChange={(event) => handleSearchInput('bol_number', event.target.value)}
            />
          </div>
          <select
            className={`${styles.filterSelect} ${selectedFilterOption ? styles.filterSelectActive : ''}`}
            value={selectedFilterOption}
            onChange={(event) => handleFilterOptionChange(event.target.value)}
            aria-label="Filter loads"
          >
            <option value="">All loads</option>
            {operationalFilters.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          <Button className={styles.controlAction} variant="ghost" size="md" onClick={() => setIsSearchRowVisible((prev) => !prev)}>
            Toggle column search
          </Button>
        </div>
      </div>

      <Surface tone="glass" padding="lg">
        <div className={styles.metrics}>
          <div className={styles.metricTile}>
            <span className={styles.metricLabel}>Loads this week</span>
            <span className={styles.metricValue}>{statValue(loadsThisWeek)}</span>
            <span className={styles.metricSubtext}>Pickup scheduled Monday through Sunday</span>
          </div>
          <div className={styles.metricTile}>
            <span className={styles.metricLabel}>Bookings confirmed</span>
            <span className={styles.metricValue}>{statValue(
              bookingsConfirmed === null || bookingsConfirmed === undefined ||
              bookingsTotal === null || bookingsTotal === undefined
                ? null
                : `${bookingsConfirmed}/${bookingsTotal}`
            )}</span>
            <span className={styles.metricSubtext}>Active shipments with an assigned carrier</span>
          </div>
          <div className={styles.metricTile}>
            <span className={styles.metricLabel}>Loads with issues</span>
            <span className={styles.metricValue}>{statValue(loadsWithIssues)}</span>
            <span className={styles.metricSubtext}>Escalations needing intervention</span>
          </div>
          <div className={styles.metricTile}>
            <span className={styles.metricLabel}>On-time delivery</span>
            <span className={styles.metricValue}>{statValue(onTimeDeliveryPercentage === null ? null : Math.round(onTimeDeliveryPercentage), '%')}</span>
            <span className={styles.metricSubtext}>Lifetime delivered shipments with known results</span>
          </div>
        </div>
      </Surface>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={sortableColumns.has(column.key) ? styles.sortableHeader : undefined}>
                  <button
                    type="button"
                    className={styles.headerButton}
                    disabled={!sortableColumns.has(column.key)}
                    onClick={() => requestSort(column.key)}
                    aria-label={sortableColumns.has(column.key) ? `${
                      !sortConfig || sortConfig.key !== column.key ? 'Sort' :
                        sortConfig.direction === 'ascending' ? 'Sort' : 'Clear'
                    } ${column.label.toLowerCase()} ${
                      !sortConfig || sortConfig.key !== column.key ? 'ascending' :
                        sortConfig.direction === 'ascending' ? 'descending' : 'sort'
                    }` : undefined}
                  >
                    {column.label}
                    {sortableColumns.has(column.key) && renderSortIndicator(column.key)}
                  </button>
                </th>
              ))}
            </tr>
            <tr
              className={`${styles.searchRow} ${
                isSearchRowVisible ? styles.searchRowVisible : styles.searchRowHidden
              }`}
            >
              {columns.map((column) => (
                <td key={column.key} className={styles.searchField}>
                  <input
                    type="text"
                    value={searchQueries[column.key]}
                    onChange={(event) => handleSearchInput(column.key, event.target.value)}
                    placeholder={`Filter ${column.label.toLowerCase()}`}
                  />
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredLoads.map((load) => (
              <React.Fragment key={load.id}>
                <tr onClick={() => toggleLoadExpansion(load.id)}>
                  <td>{getReferenceValue(load, 'bol') || '—'}</td>
                  <td>{formatStopSchedule(load, 'pickup')}</td>
                  <td>{load.lane || '—'}</td>
                  <td>{load.carrier_name || '—'}</td>
                  <td>{(load.status || '—').toUpperCase()}</td>
                  <td>{load.current_location || '—'}</td>
                  <td>{formatStopSchedule(load, 'delivery')}</td>
                  <td className={styles.remarkCell} title={load.last_remark || undefined}>{load.last_remark || '—'}</td>
                </tr>
                {expandedLoadId === load.id && (
                  <tr className={styles.expandRow}>
                    <td colSpan={columns.length}>
                      <div className={styles.expandContent}>
                        <ShipmentSummaryDrawer
                          load={loadDetailsContent[load.id] || load}
                          isLoading={Boolean(loadingDetails[load.id])}
                          error={Boolean(detailErrors[load.id])}
                          onOpenWorkspace={() => handleOpenWorkspace(load.id)}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.deliveredSection}>
        <button className={styles.deliveredToggle} type="button" onClick={() => setShowDeliveredLoads((prev) => !prev)}>
          {showDeliveredLoads ? <FiChevronUp /> : <FiChevronDown />} {showDeliveredLoads ? 'Hide' : 'Show'} delivered loads
        </button>
        {showDeliveredLoads && (
          <Surface tone="glass" padding="lg">
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <th key={column.key}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {deliveredLoads.map((load) => (
                    <React.Fragment key={load.id}>
                      <tr onClick={() => toggleDeliveredLoadExpansion(load.id)}>
                        <td>{getReferenceValue(load, 'bol') || '—'}</td>
                        <td>{formatStopSchedule(load, 'pickup')}</td>
                        <td>{load.lane || '—'}</td>
                        <td>{load.carrier_name || '—'}</td>
                        <td>{load.status || '—'}</td>
                        <td>{load.current_location || '—'}</td>
                        <td>{formatStopSchedule(load, 'delivery')}</td>
                        <td className={styles.remarkCell} title={load.last_remark || undefined}>{load.last_remark || '—'}</td>
                      </tr>
                      {expandedDeliveredLoadId === load.id && (
                        <tr className={styles.expandRow}>
                          <td colSpan={columns.length}>
                            <div className={styles.expandContent}>
                              <ShipmentSummaryDrawer
                                load={loadDetailsContent[load.id] || load}
                                isLoading={Boolean(loadingDetails[load.id])}
                                error={Boolean(detailErrors[load.id])}
                                onOpenWorkspace={() => handleOpenWorkspace(load.id)}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Surface>
        )}
      </div>
    </div>
  );

  const renderPlaceholder = (title, subtitle) => (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.tabRow}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.tabButton} ${selectedTab === tab.key ? styles.tabActive : ''}`}
              onClick={() => setSelectedTab(tab.key)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <Surface tone="glass" padding="lg" style={{ textAlign: 'center' }}>
        <h3>{title}</h3>
        <p style={{ color: 'var(--color-text-secondary)', marginTop: '12px' }}>{subtitle}</p>
      </Surface>
    </div>
  );

  if (selectedTab === 'quote') {
    return renderPlaceholder(
      'Quotes & bookings refresh',
      'Real-time quoting flows are being elevated to the new design language.'
    );
  }
  if (selectedTab === 'accountsPayable') {
    return renderPlaceholder(
      'Accounts payable workspace',
      'Settlements, invoice automation, and payment approvals are coming shortly.'
    );
  }
  if (selectedTab === 'claims') {
    return renderPlaceholder('Claims assistant', 'Intelligent claim triage and resolution will appear here.');
  }
  if (selectedTab === 'stats') {
    return renderPlaceholder('Reporting studio', 'Executive dashboards and export automations are on the roadmap.');
  }

  return renderFTL();
};

export default LoadBoard;
